import { set, sumBy } from 'lodash-es'
import { defineAsyncComponent } from 'vue'

import Attachment from 'src/components/attachment/attachment.vue'

import {
  useMediaViewerStore,
  supportedTypes as viewerTypes,
} from 'src/stores/media_viewer.js'
import { useMergedConfigStore } from 'src/stores/merged_config.js'

// with its metadata reader, loaded for the first audio file
const AudioPlayer = defineAsyncComponent(
  () => import('src/components/audio_player/audio_player.vue'),
)

const displayTypes = new Set(['image', 'video', 'flash'])

// How many thumbnails a post over the "max thumbnails" limit shows
// before collapsing the rest into a "+N" tile. Must stay 2 rows x 4
// columns, matching the `-overflow` grid CSS in gallery.vue.
export const OVERFLOW_GRID_CELLS = 8

// The attachment the "+N" tile opens: the last visible cell, or the
// next one the viewer can display, or the first viewable one at all.
export const overflowTarget = (attachments, cells) => {
  const viewable = (attachment) => viewerTypes.has(attachment.type)
  return (
    attachments.slice(cells - 1).find(viewable) || attachments.find(viewable)
  )
}

const Gallery = {
  props: [
    'attachments',
    'compact',
    'limitRows',
    'descriptions',
    'limit',
    'nsfw',
    'setMedia',
    'size',
    'editable',
    'removeAttachment',
    'shiftUpAttachment',
    'shiftDnAttachment',
    'editAttachment',
    'grid',
    'overflowThreshold',
  ],
  data() {
    return {
      sizes: {},
      hidingLong: true,
      playingMedia: new Set(),
      // one reveal state for every sensitive attachment in the post
      nsfwRevealed: false,
    }
  },
  emits: ['play', 'pause'],
  components: { Attachment, AudioPlayer },
  computed: {
    hideNsfw() {
      return useMergedConfigStore().mergedConfig.hideNsfw
    },
    nsfwHidden() {
      return !!this.nsfw && !!this.hideNsfw && !this.nsfwRevealed
    },
    overflowGrid() {
      return (
        !this.editable &&
        !this.grid &&
        this.size !== 'hide' &&
        this.size !== 'small' &&
        this.overflowThreshold > 0 &&
        this.attachments?.length >
          Math.max(this.overflowThreshold, OVERFLOW_GRID_CELLS)
      )
    },
    rows() {
      if (!this.attachments) {
        return []
      }
      if (this.overflowGrid && this.hidingLong) {
        return [
          {
            grid: true,
            overflow: this.attachments.length - OVERFLOW_GRID_CELLS,
            items: this.attachments.slice(0, OVERFLOW_GRID_CELLS),
          },
        ]
      }
      const attachments =
        this.limit > 0
          ? this.attachments.slice(0, this.limit)
          : this.attachments
      if (this.size === 'hide') {
        return attachments.map((item) => ({ minimal: true, items: [item] }))
      }
      // With the player, all audio goes into one row placed where the
      // first track was; the row is widened after packing so the image
      // rows come out the same as with a single audio file.
      const grouped = this.useAudioPlayer
      const audio = grouped
        ? attachments.filter((item) => item.type === 'audio')
        : []
      const sequence = grouped
        ? attachments.filter(
            (item) => item.type !== 'audio' || item === audio[0],
          )
        : attachments
      const rows = this.grid
        ? [{ grid: true, items: attachments }]
        : sequence
            .reduce(
              (acc, attachment, i) => {
                const peek = sequence[i + 1]
                const nextEnd = peek == null
                const nextWide = !nextEnd && !displayTypes.has(peek?.type)

                // Inserting new row
                if (attachment.type === 'audio') {
                  return [
                    ...acc,
                    { audio: true, items: [attachment] },
                    { items: [] },
                  ]
                }
                if (!displayTypes.has(attachment.type)) {
                  return [
                    ...acc,
                    { minimal: true, items: [attachment] },
                    { items: [] },
                  ]
                }

                const maxPerRow = 3
                const currentRow = acc[acc.length - 1]
                const previousRow = acc[acc.length - 2]

                if (currentRow.items.length >= maxPerRow) {
                  if (nextWide || nextEnd) {
                    if (previousRow?.items.length > 1) {
                      currentRow.items.push(attachment)
                      return [...acc, { items: [] }]
                    } else {
                      const last = currentRow.items.splice(-1)[0]
                      return [...acc, { items: [last, attachment] }]
                    }
                  } else {
                    return [...acc, { items: [attachment] }]
                  }
                } else {
                  currentRow.items.push(attachment)
                }
                return acc
              },
              [{ items: [] }],
            )
            .filter((_) => _.items.length > 0)
      return grouped
        ? rows.map((row) => (row.audio ? { ...row, items: audio } : row))
        : rows
    },
    attachmentsDimensionalScore() {
      return this.rows.reduce((acc, row) => {
        let size = 0
        if (row.minimal) {
          size += 1 / 8
        } else if (row.audio) {
          size += 1 / 4
        } else {
          size += 1 / (row.items.length + 0.6)
        }
        return acc + size
      }, 0)
    },
    tooManyAttachments() {
      if (this.editable || this.size === 'small') {
        return false
      } else if (this.overflowGrid) {
        return true
      } else if (this.size === 'hide') {
        return this.attachments.length > 8
      } else {
        return this.attachmentsDimensionalScore > 1
      }
    },
    // The compact player replaces per-attachment audio rendering unless the
    // gallery is being edited, is behind an nsfw click-through, or is tiny
    useAudioPlayer() {
      return (
        !this.editable && !this.nsfw && (!this.size || this.size === 'normal')
      )
    },
    hasPlayingMedia() {
      return this.playingMedia.size > 0
    },
  },
  methods: {
    onNaturalSizeLoad({ id, width, height }) {
      set(this.sizes, id, { width, height })
    },
    onMediaStateChange(playing, id) {
      if (playing) {
        this.playingMedia.add(id)
      } else {
        this.playingMedia.delete(id)
      }
    },
    rowStyle(row) {
      if (row.audio && !this.useAudioPlayer) {
        return { 'padding-bottom': '25%' } // fixed reduced height for audio
      } else if (!row.minimal && !row.grid && !row.audio) {
        return { 'padding-bottom': `${100 / (row.items.length + 0.6)}%` }
      }
    },
    itemStyle(id, row) {
      const total = sumBy(row, (item) => this.getAspectRatio(item.id))
      return { flex: `${this.getAspectRatio(id) / total} 1 0%` }
    },
    getAspectRatio(id) {
      const size = this.sizes[id]
      return size ? size.width / size.height : 1
    },
    toggleHidingLong(event) {
      this.hidingLong = event
    },
    setNsfwRevealed(value) {
      this.nsfwRevealed = value
    },
    onOverlayClick() {
      if (useMergedConfigStore().mergedConfig.useOneClickNsfw) {
        this.openGallery()
      } else {
        this.setNsfwRevealed(true)
      }
    },
    openGallery() {
      useMediaViewerStore().setMedia(this.attachments)
      useMediaViewerStore().setCurrentMedia(this.attachments[0])
    },
    openOverflow() {
      const target = overflowTarget(this.attachments, OVERFLOW_GRID_CELLS)
      if (!target) return
      useMediaViewerStore().setMedia(this.attachments)
      useMediaViewerStore().setCurrentMedia(target)
    },
    onMedia() {
      useMediaViewerStore().setMedia(this.attachments)
    },
  },
  watch: {
    hasPlayingMedia(newValue) {
      if (newValue) {
        this.$emit('play')
      } else {
        this.$emit('pause')
      }
    },
  },
}

export default Gallery
